import React, { Component } from 'react';
import styled from 'styled-components';
import {
  Button,
  ErrorBanner,
  Link,
  PieChart,
  GlobalErrorComponent,
} from '@trp-rps-rps-shared-web/rps-shared-web-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import { connect } from 'react-redux';
import { modalActions, accountActions } from '../../../redux/root-action';
import ReactModalFooter from '../../Modal/ReactModalFooter';
import ReactModalHeader from '../../Modal/ReactModalHeader';
import ReactModalContent from '../../Modal/ReactModalContent';
import { modals, accountModalViews } from '../../../utils/constants';
import {
  formatAmountWithoutDecimals,
  annualERContTooltip,
  annualEEContTooltip,
  isEligbleForEEContribution,
  isEligbleForERContribution,
  keyDown,
} from '../../../utils/helpers';
import { Tooltip } from '@trp-rps-rps-shared-web/rps-shared-web-components';

const StyledFieldContainer = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 1px solid #e1e5e8;
  color: ${tokens.ColorGrey900};
  justify-content: space-between;
  @media only screen and (max-width: 767px) {
    margin-top: 16px;
  }
`;

const StyledInputLabel = styled.div`
  margin-right: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media only screen and (max-width: 767px) {
    height: auto;
    width: auto;
  }
`;

const StyledIntroLine = styled.div`
  font-size: 20px;
  margin-top: 32px;
  color: ${tokens.ColorGrey900};
  @media only screen and (max-width: 767px) {
    font-size: 16px;
    margin-top: 24px;
  }
`;

const StyledText = styled.div`
  margin: 32px 0px 32px 0px;
  display: flex;
  outline: none;
  @media only screen and (max-width: 767px) {
    margin: 32px 0px 32px 0px;
  }
`;

const StyledValue = styled.div`
  font-family: Nimbus Sans Bold;
  margin: 16px 0px 16px 0px;
  text-align: right;
  @media only screen and (max-width: 767px) {
    margin: 16px 0px 16px 0px;
  }
`;

const StyledDelete = styled.div`
  display: flex;
  align-items: center;
  button {
    cursor: pointer;
    padding-left: 0;
  }
  img {
    width: 24px;
    height: 24px;
  }
`;

const StyledInvestmentMixContainer = styled.div`
  display: flex;
  padding-top: 24px;
`;

const StyledEllipse = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin: 6px 8px 8px 8px;
`;

const StyledInvestMix = styled.div`
  margin: 16px 0px 48px 0px;
`;

const StyledAssetAllocation = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
`;

const StyledPieChartContainer = styled.div`
  margin: 0px 24px 0px 24px;
`;

const StyledInvestmentMixFields = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 8px 0px;
`;

const StyledError = styled.span`
  color: ${tokens.ColorFunctionalRed500};
`;

class AccountInfo extends Component<any, any> {
  showAddAccountModal = () => {
    const { showModal, setCurrentView } = this.props;
    showModal(modals.ACCOUNT);
    setCurrentView(accountModalViews.ADD_ACCOUNT);
  };

  showDeleteAccountModal = () => {
    const { showModal, setCurrentView } = this.props;
    showModal(modals.ACCOUNT);
    setCurrentView(accountModalViews.DELETE_ACCOUNT_MODAL);
  };

  handleCloseIconClick = () => {
    const { setCurrentView } = this.props;
    setCurrentView(accountModalViews.YOUR_ACCOUNTS);
  };

  render(): JSX.Element {
    const mq = window.matchMedia('(max-width: 767px)');
    const investmentMixFields = [
      {
        value: 'stockPercent',
        displayName: 'Stocks',
        color: tokens.ColorBrandInteractiveBlue,
      },
      {
        value: 'bondsPercent',
        displayName: 'Bonds',
        color: tokens.ColorChartingBlue500,
      },
      {
        value: 'moneyMarketPercent',
        displayName: 'Short-Term',
        color: tokens.ColorChartingTeal100,
      },
      {
        value: 'otherPercent',
        displayName: 'Other',
        color: tokens.ColorChartingPurple500,
      },
    ];

    const {
      name,
      displayType,
      accountHolder,
      balance,
      assetAllocation,
      startNumber,
      typeName,
      amount,
      hasYodleeError,
      isYodleeAccount,
      isPensionAccount,
      annualSavingsEE,
      annualSavingsER,
      hasYodleeAssetAllocationError,
      contributionType,
    } = this.props.account;
    const showError = false;
    const isNonQual = displayType.indexOf('Non-Qualified') > -1;
    return (
      <React.Fragment>
        <ReactModalHeader
          modalTitle="Account Information"
          showClose={true}
          handleCloseIconClick={this.handleCloseIconClick}
        />
        <ReactModalContent>
          {showError ? (
            <>
              <GlobalErrorComponent
                message={`We’re having trouble displaying your accounts. Please try again.`}
                refreshText={`Refresh List`}
                hideOverlay={true}
              />{' '}
            </>
          ) : (
            <>
              {hasYodleeError && (
                <ErrorBanner>
                  There are one or more errors. To correct them,
                  <Link
                    linkLabel={'edit account details.'}
                    onClick={this.showAddAccountModal}
                    tabIndex={0}
                    onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showAddAccountModal)}
                    color={tokens.ColorBrandInteractiveBlue}
                    hoverColor={tokens.ColorBrandInteractiveBlueHover}
                    customStyles={'display: inline; text-decoration: underline; margin-left: 4px;'}
                  />
                </ErrorBanner>
              )}
              <StyledIntroLine tabIndex={0}>
                This information will be included in your retirement savings balances and projections throughout the
                site. Please review it regularly to make sure it’s up to date.
              </StyledIntroLine>
              <StyledText
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showAddAccountModal)}
                onClick={this.showAddAccountModal}
              >
                <Link linkLabel="Edit account details" customStyles={'font-family: Nimbus Sans Bold'} />
                <img
                  width="16"
                  height="16"
                  style={{ marginLeft: 8 }}
                  src={`data:image/svg+xml;base64,${tokens.AssetIconEdit}`}
                  alt="edit icon"
                />
              </StyledText>
              <StyledFieldContainer>
                <StyledInputLabel tabIndex={0}>Account Type</StyledInputLabel>
                <StyledValue tabIndex={0}>{displayType ? displayType : <StyledError>Error</StyledError>}</StyledValue>
              </StyledFieldContainer>
              <StyledFieldContainer>
                <StyledInputLabel tabIndex={0}>Account Name</StyledInputLabel>
                <StyledValue tabIndex={0}>{name}</StyledValue>
              </StyledFieldContainer>
              {!isPensionAccount && (
                <StyledFieldContainer>
                  <StyledInputLabel tabIndex={0}>Balance</StyledInputLabel>
                  <StyledValue tabIndex={0}>{formatAmountWithoutDecimals(balance)}</StyledValue>
                </StyledFieldContainer>
              )}
              {!isNonQual && (
                <>
                  {!isPensionAccount && !isYodleeAccount && (
                    <React.Fragment>
                      {isEligbleForERContribution(contributionType) && (
                        <StyledFieldContainer>
                          <StyledInputLabel
                            aria-describedby="annual-savings-tooltip"
                            tabIndex={0}
                            data-tip={annualERContTooltip()}
                          >
                            Annual Employer
                            <br />
                            Contribution
                            <Tooltip />
                            <img
                              src={`data:image/svg+xml;base64,${tokens.AssetImageDashedLine}`}
                              alt="dashed-underline"
                            />
                          </StyledInputLabel>
                          <StyledValue tabIndex={0}>{formatAmountWithoutDecimals(annualSavingsER)}</StyledValue>
                        </StyledFieldContainer>
                      )}
                      {isEligbleForEEContribution(contributionType) && (
                        <StyledFieldContainer>
                          <StyledInputLabel
                            aria-describedby="annual-savings-tooltip"
                            tabIndex={0}
                            data-tip={annualEEContTooltip()}
                          >
                            Account Owner&apos;s <br />
                            Annual Savings
                            <Tooltip />
                            <img
                              src={`data:image/svg+xml;base64,${tokens.AssetImageDashedLine}`}
                              alt="dashed-underline"
                            />
                          </StyledInputLabel>
                          <StyledValue tabIndex={0}>{formatAmountWithoutDecimals(annualSavingsEE)}</StyledValue>
                        </StyledFieldContainer>
                      )}
                    </React.Fragment>
                  )}
                </>
              )}
              <StyledFieldContainer>
                <StyledInputLabel tabIndex={0}>Owner</StyledInputLabel>
                <StyledValue tabIndex={0} className="blocked_data">
                  {accountHolder}
                </StyledValue>
              </StyledFieldContainer>
              {isPensionAccount ? (
                <React.Fragment>
                  <StyledFieldContainer>
                    <StyledInputLabel tabIndex={0}>Payment Method</StyledInputLabel>
                    <StyledValue tabIndex={0}>{typeName.includes('Monthly') ? 'Monthly ' : 'Lumpsum '}</StyledValue>
                  </StyledFieldContainer>
                  <StyledFieldContainer>
                    <StyledInputLabel tabIndex={0}>
                      {typeName.includes('Monthly') && 'Start'} Age and Amount
                    </StyledInputLabel>
                    <StyledValue tabIndex={0}>
                      {startNumber} &nbsp;{' '}
                      {isPensionAccount ? formatAmountWithoutDecimals(amount) : formatAmountWithoutDecimals(balance)}{' '}
                    </StyledValue>{' '}
                  </StyledFieldContainer>
                </React.Fragment>
              ) : (
                <StyledInvestMix>
                  <StyledInputLabel>Investment Mix</StyledInputLabel>
                  <StyledInvestmentMixContainer>
                    <StyledPieChartContainer>
                      <PieChart
                        data={assetAllocation && investmentMixFields.map(({ value }) => assetAllocation[value] * 100)}
                      />
                    </StyledPieChartContainer>
                    <StyledAssetAllocation>
                      {investmentMixFields.map(({ color, displayName, value }) => (
                        <StyledInvestmentMixFields key={displayName}>
                          <div style={{ display: 'flex' }}>
                            <StyledEllipse color={color} /> <span>{displayName}</span>
                          </div>
                          <div>
                            <b>
                              {hasYodleeAssetAllocationError ? (
                                <StyledError>Error</StyledError>
                              ) : assetAllocation ? (
                                assetAllocation[value] + '%'
                              ) : (
                                ''
                              )}
                            </b>
                          </div>
                        </StyledInvestmentMixFields>
                      ))}
                    </StyledAssetAllocation>
                  </StyledInvestmentMixContainer>
                </StyledInvestMix>
              )}
            </>
          )}
        </ReactModalContent>
        {!isYodleeAccount && (
          <ReactModalFooter hideCancel={true} customStyles={'justify-content: flex-start'}>
            <StyledDelete>
              <Button
                type="textButton"
                title="Delete Account"
                fitContainerWidth={mq.matches ? true : false}
                icon={tokens.AssetIconDeleteOutline}
                marginLeftPx={0}
                onClick={this.showDeleteAccountModal}
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showDeleteAccountModal)}
              />
            </StyledDelete>
          </ReactModalFooter>
        )}
      </React.Fragment>
    );
  }
}

export default connect(
  (state: any) => ({
    account: state.account.selectedAccount,
  }),
  {
    hideModal: modalActions.hideModal,
    showModal: modalActions.showModal,
    setCurrentView: accountActions.setCurrentView,
  },
)(AccountInfo);
